/**
 * BST Product Feature Highlights — horizontal scroll + pagination dots.
 */
const BST_FEATURE_HIGHLIGHTS_SELECTORS = {
  track: '[data-feature-highlights-track]',
  item: '.bst-product-feature-highlights__item',
  pagination: '[data-feature-highlights-pagination]',
};

class BstProductFeatureHighlightsComponent extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.pagination = null;
    this.items = [];
    this.dots = [];
    this.pageCount = 0;
    this.activePage = 0;
    this.perPage = 1;

    this.onDotClick = this.onDotClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onSectionLoad = this.onSectionLoad.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector(BST_FEATURE_HIGHLIGHTS_SELECTORS.track);
    this.pagination = this.querySelector(BST_FEATURE_HIGHLIGHTS_SELECTORS.pagination);

    if (!this.track || !this.pagination) return;

    this.items = [...this.track.querySelectorAll(BST_FEATURE_HIGHLIGHTS_SELECTORS.item)];
    if (this.items.length < 2) {
      this.pagination.hidden = true;
      return;
    }

    this.buildDots();
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => this.buildDots());
    });

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    document.addEventListener('shopify:section:load', this.onSectionLoad);

    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
    }
  }

  disconnectedCallback() {
    if (this.track) {
      this.track.removeEventListener('scroll', this.onScroll);
    }
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onSectionLoad(event) {
    if (!event.target.contains(this)) return;
    this.items = [...this.track.querySelectorAll(BST_FEATURE_HIGHLIGHTS_SELECTORS.item)];
    this.buildDots();
  }

  onResize() {
    if (this._resizeTimer) window.clearTimeout(this._resizeTimer);
    this._resizeTimer = window.setTimeout(() => this.buildDots(), 150);
  }

  onDotClick(event) {
    const page = parseInt(event.currentTarget.dataset.featureHighlightsDot, 10);
    if (Number.isNaN(page)) return;
    this.goToPage(page);
  }

  onScroll() {
    if (this._scrollRaf) return;
    this._scrollRaf = window.requestAnimationFrame(() => {
      this._scrollRaf = null;
      this.updateActivePage();
    });
  }

  isScrollable() {
    return this.track.scrollWidth > this.track.clientWidth + 1;
  }

  getMetrics() {
    const item = this.items[0];
    if (!item) return { perPage: 1, pageCount: 0 };

    const styles = window.getComputedStyle(this.track);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const itemWidth = item.offsetWidth;
    const trackWidth = this.track.clientWidth;
    const perPage = Math.max(1, Math.floor((trackWidth + gap) / (itemWidth + gap)));
    const pageCount = Math.ceil(this.items.length / perPage);

    return { perPage, pageCount, itemWidth, gap };
  }

  buildDots() {
    if (!this.pagination || this.items.length < 2) return;

    this.pagination.innerHTML = '';
    this.dots = [];

    if (!this.isScrollable()) {
      this.pagination.hidden = true;
      this.pageCount = 0;
      return;
    }

    const { perPage, pageCount } = this.getMetrics();
    this.perPage = perPage;
    this.pageCount = pageCount;

    if (pageCount <= 1) {
      this.pagination.hidden = true;
      return;
    }

    this.pagination.hidden = false;

    for (let i = 0; i < pageCount; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'bst-product-feature-highlights__pagination-dot';
      dot.dataset.featureHighlightsDot = String(i);
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
      dot.addEventListener('click', this.onDotClick);
      this.pagination.appendChild(dot);
      this.dots.push(dot);
    }

    this.updateActivePage(true);
  }

  getScrollOffsetForPage(page) {
    const index = Math.min(page * this.perPage, this.items.length - 1);
    const item = this.items[index];
    return Math.max(0, item.offsetLeft - this.track.offsetLeft);
  }

  goToPage(page) {
    const clamped = Math.max(0, Math.min(page, this.pageCount - 1));
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    this.track.scrollTo({
      left: this.getScrollOffsetForPage(clamped),
      behavior,
    });

    this.setActivePage(clamped);
  }

  updateActivePage(immediate = false) {
    if (!this.dots.length) return;

    const { perPage, itemWidth, gap } = this.getMetrics();
    this.perPage = perPage;

    const scrollLeft = this.track.scrollLeft;
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const step = perPage * (itemWidth + gap);

    let page = 0;
    if (maxScroll <= 0) {
      page = 0;
    } else if (scrollLeft >= maxScroll - 2) {
      page = this.pageCount - 1;
    } else {
      page = Math.round(scrollLeft / step);
    }

    page = Math.max(0, Math.min(page, this.pageCount - 1));
    this.setActivePage(page, immediate);
  }

  setActivePage(page, immediate = false) {
    if (page === this.activePage && !immediate) return;

    this.activePage = page;

    this.dots.forEach((dot, index) => {
      const isActive = index === page;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('bst-product-feature-highlights-component')) {
  customElements.define('bst-product-feature-highlights-component', BstProductFeatureHighlightsComponent);
}
